import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  ShieldCheck, ArrowRight, Lock, CheckCircle2, Sparkles,
  Loader2,
} from 'lucide-react';
import ScamIQBadge from '../components/ScamIQBadge';
import PremiumScenarioCard from '../components/PremiumScenarioCard';
import { API_URL } from '../config';
import { trackViewContent, trackInitiateCheckout, trackLead } from '../utils/metaPixel';
import '../styles/ScamSmart.css';

const API = `${API_URL}/api`;
const SHIELD_LOGO = 'https://customer-assets.emergentagent.com/job_express-checkout-9/artifacts/gw8x19qe_Variant%201_Shield%20Only_Vector%20copy.png';

const CATEGORY_LABEL = {
  core: 'Core scam patterns',
  family_emergency: 'Family emergency / new number',
  banking_government: 'Banking & government impersonation',
  delivery_shopping: 'Delivery & online shopping',
  marketplace_payid: 'Marketplace / PayID',
  account_security: 'Account security',
  emerging: 'Emerging patterns',
  general: 'General awareness',
};
const labelFor = (k) => CATEGORY_LABEL[k] || k || 'General awareness';

const rankFor = (score) => {
  if (score >= 90) return 'Human Firewall';
  if (score >= 75) return 'Hard to Fool';
  if (score >= 60) return 'Street Smart';
  if (score >= 40) return 'Getting Sharper';
  return 'Needs Practice';
};

const ScamSmartLanding = () => {
  const [stage, setStage] = useState('landing'); // landing | testing | preview
  const [freeScenarios, setFreeScenarios] = useState([]);
  const [idx, setIdx] = useState(0);
  const [results, setResults] = useState([]); // [{scenario, result, points, ruleUnlocked}]
  const [loadingScenarios, setLoadingScenarios] = useState(false);
  const [email, setEmail] = useState('');
  const [emailErr, setEmailErr] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Meta Pixel — fire ViewContent once on landing page mount
  useEffect(() => {
    trackViewContent({
      content_name: 'ScamSmart — Family Scam Defence',
      content_id: 'scamsmart-access',
      value: 14.99,
    });
  }, []);

  // Load 3 free V2 scenarios on demand
  const startTest = async () => {
    setLoadingScenarios(true);
    try {
      const { data } = await axios.get(`${API}/scamsmart/v2/scenarios/free`);
      setFreeScenarios(data.scenarios || []);
      setIdx(0);
      setResults([]);
      setStage('testing');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      alert('Could not load scenarios — please try again.');
    } finally {
      setLoadingScenarios(false);
    }
  };

  const handleScenarioComplete = (r) => {
    const newResults = [...results, r];
    setResults(newResults);
    if (idx + 1 < freeScenarios.length) {
      setIdx(idx + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      setStage('preview');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const previewMetrics = useMemo(() => {
    if (!results.length) return null;
    const totalPts = results.reduce((s, r) => s + (r.points || 0), 0);
    const maxPts = results.length * 10;
    const score = Math.round((totalPts / maxPts) * 100);
    const safe = results.filter(r => r.result === 'safe').length;
    const risky = results.filter(r => r.result === 'risky').length;
    const dangerous = results.filter(r => r.result === 'dangerous').length;
    const strong = results.find(r => r.result === 'safe')?.scenario?.category || 'general';
    const weak = results.find(r => r.result !== 'safe')?.scenario?.category || results[0].scenario.category;
    return { score, rank: rankFor(score), safe, risky, dangerous, strong, weak };
  }, [results]);

  const submitEmailAndUnlock = async (e) => {
    e?.preventDefault?.();
    setEmailErr('');
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      setEmailErr('Please enter a valid email so we can save your progress and email your access.');
      return;
    }
    setSubmitting(true);
    try {
      // Meta Pixel — fire Lead (email captured after free preview)
      trackLead({
        lead_type: 'scamsmart_free_preview_completed',
        content_name: 'ScamSmart — Free Preview Completed',
        value: previewMetrics?.score || 0,
      });
      await axios.post(`${API}/scamsmart/leads/free-completed`, {
        email,
        safe_count: previewMetrics?.safe || 0,
        risky_count: previewMetrics?.risky || 0,
        dangerous_count: previewMetrics?.dangerous || 0,
        weak_category: previewMetrics?.weak,
        strong_category: previewMetrics?.strong,
      }).catch(() => {});
      // Meta Pixel — fire InitiateCheckout before redirecting to Stripe
      trackInitiateCheckout({
        content_name: 'ScamSmart — Family Scam Defence',
        content_id: 'scamsmart-access',
        value: 14.99,
      });
      const { data } = await axios.post(`${API}/scamsmart/checkout`, {
        customer_email: email,
        utm_params: { source: 'free-test' },
      });
      window.location.href = data.url;
    } catch (e) {
      setEmailErr('Sorry — checkout could not start. Please try again or contact support.');
      setSubmitting(false);
    }
  };

  const currentScenario = freeScenarios[idx];

  return (
    <div className="ss-page" data-testid="scamsmart-page">
      <header className="ss-header">
        <div className="ss-header-inner">
          <Link to="/" className="ss-brand" data-testid="ss-brand">
            <img src={SHIELD_LOGO} alt="" className="ss-brand-mark" />
            <div>
              <div className="ss-brand-text">All Secured</div>
              <div className="ss-brand-sub">ScamSmart</div>
            </div>
          </Link>
          {stage === 'landing' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <ScamIQBadge />
              <button className="ss-header-cta" onClick={startTest} data-testid="ss-header-start-btn">
                Free Scam IQ Test
              </button>
            </div>
          )}
          {stage !== 'landing' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <ScamIQBadge />
              <Link to="/parents-handbook" className="ss-header-cta">Handbook →</Link>
            </div>
          )}
        </div>
      </header>

      {stage === 'landing' && (
        <>
          <section className="ss-hero" data-testid="ss-hero">
            <div className="ss-hero-inner">
              <div>
                <span className="ss-pill">
                  <Sparkles size={12} /> By All Secured
                </span>
                <h1 className="ss-h1">What's your Scam IQ?</h1>
                <p className="ss-sub">
                  ScamSmart is a premium scam-awareness and decision-training tool. Each scenario walks
                  you through a real moment, the actual message on the screen, the clues hiding in plain
                  sight, and what would happen next — calmly, the way a friend would explain it.
                </p>
                <p className="ss-supporting">
                  Most scams don't work because people are careless. They work because they arrive at the wrong
                  moment — with urgency, emotion, and just enough detail to feel real. ScamSmart helps you
                  practise the pause-before-you-respond habit in a calm, practical way.
                </p>
                <div className="ss-cta-row">
                  <button className="ss-btn-primary" onClick={startTest} disabled={loadingScenarios} data-testid="ss-hero-cta">
                    {loadingScenarios ? <Loader2 size={18} className="ss-spin" /> : <>Take the Free Scam IQ Test <ArrowRight size={18} /></>}
                  </button>
                  <a href="#how" className="ss-btn-ghost">How ScamSmart works</a>
                </div>
                <p className="ss-microtrust">
                  3 free scenarios &middot; no signup &middot; ~3 minutes
                </p>
              </div>

              <aside className="ss-hero-card" aria-hidden="true">
                <div className="ss-hero-iq">Sample Scam IQ</div>
                <div className="ss-hero-score">82</div>
                <div className="ss-hero-rank">Hard to Fool</div>
                <div className="ss-hero-card-divider"></div>
                <p className="ss-hero-tag">Sample SMS Sarah just received</p>
                <p className="ss-hero-msg">
                  "Hi Mum, dropped my phone — this is my temp number. Can you transfer $850 for rent today? Don't tell Dad."
                </p>
                <div className="ss-hero-pill-row">
                  <span className="ss-hero-pill">New number</span>
                  <span className="ss-hero-pill">Urgency</span>
                  <span className="ss-hero-pill">"Don't tell"</span>
                </div>
              </aside>
            </div>
          </section>

          <section className="ss-section" id="how" data-testid="ss-how-it-works">
            <div className="ss-section-eyebrow">How it works</div>
            <h2 className="ss-section-title">Set the scene. Read the message. Decide. Learn.</h2>
            <p className="ss-section-lede">
              Every ScamSmart scenario is a 90-second walk through one realistic moment — with the actual
              SMS, email, or call surface; the clues you'd find if you slowed down; and a calm explanation of
              what would have happened with each choice.
            </p>
            <div className="ss-steps">
              <div className="ss-step">
                <div className="ss-step-num">01</div>
                <div className="ss-step-title">Step into the moment</div>
                <p className="ss-step-body">Time of day, what you were doing, and the real message — rendered the way it actually appears, not paraphrased.</p>
              </div>
              <div className="ss-step">
                <div className="ss-step-num">02</div>
                <div className="ss-step-title">Investigate the clues</div>
                <p className="ss-step-body">Open the optional clue panel to see what an expert would notice — the sender, the timing, the payment ask.</p>
              </div>
              <div className="ss-step">
                <div className="ss-step-num">03</div>
                <div className="ss-step-title">Decide — and see the consequence</div>
                <p className="ss-step-body">Each option triggers a three-part breakdown: what would happen, what the scammer wanted, and why this works on normal people.</p>
              </div>
            </div>
            <div className="ss-cta-row" style={{ marginTop: '32px' }}>
              <button className="ss-btn-primary" onClick={startTest} data-testid="ss-how-cta">
                Take the Free Scam IQ Test <ArrowRight size={18} />
              </button>
            </div>
          </section>

          <section className="ss-section" style={{ paddingTop: 0 }}>
            <div className="ss-trusted">
              <div className="ss-trusted-title">Trusted guidance</div>
              ScamSmart scenarios are built around common scam patterns and public guidance from trusted
              Australian consumer-safety and cyber-security organisations — Scamwatch, cyber.gov.au, the
              ACCC, ASIC, Services Australia, IDCARE, and major Australian banks. Every scenario carries a
              source-basis tag and a last-reviewed date so the content is maintained over time.
              <br /><br />
              <strong>If you believe you have transferred money or shared financial information,</strong> contact
              your bank immediately. You can also contact <a href="https://www.idcare.org" target="_blank" rel="noopener noreferrer">IDCARE</a> for recovery support and
              report scams to <a href="https://www.scamwatch.gov.au" target="_blank" rel="noopener noreferrer">Scamwatch</a>.
            </div>
            <p className="ss-disclaimer">
              ScamSmart is an awareness and decision-training tool. It cannot detect every scam or guarantee protection.
              Always verify important requests through official or trusted channels.
            </p>
          </section>
        </>
      )}

      {stage === 'testing' && currentScenario && (
        <div className="ss-test-wrap" data-testid="ss-scenario-screen">
          <div className="ss-test-container">
            <div className="ss-progress">
              <span>Scenario {idx + 1} of {freeScenarios.length}</span>
              <span>Free preview</span>
            </div>
            <div className="ss-progress-bar">
              <div className="ss-progress-bar-fill" style={{ width: `${((idx) / freeScenarios.length) * 100}%` }} />
            </div>
            <PremiumScenarioCard
              key={currentScenario.id}
              scenario={currentScenario}
              onComplete={handleScenarioComplete}
            />
          </div>
        </div>
      )}

      {stage === 'preview' && previewMetrics && (
        <PreviewAndPaywall
          metrics={previewMetrics}
          email={email}
          setEmail={setEmail}
          emailErr={emailErr}
          submitting={submitting}
          onSubmit={submitEmailAndUnlock}
        />
      )}
    </div>
  );
};

// === Preview + paywall =====================================================
const PreviewAndPaywall = ({ metrics, email, setEmail, emailErr, submitting, onSubmit }) => {
  const [showBypass, setShowBypass] = useState(false);
  const [bypassCode, setBypassCode] = useState('');
  const [bypassErr, setBypassErr] = useState('');
  const [bypassSubmitting, setBypassSubmitting] = useState(false);

  const submitBypass = async (e) => {
    e?.preventDefault?.();
    setBypassErr('');
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      setBypassErr('Please enter your email above first.');
      return;
    }
    if (!bypassCode.trim()) {
      setBypassErr('Enter your early-access code.');
      return;
    }
    setBypassSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/scamsmart/dev/grant-access`, {
        email, token: bypassCode.trim(),
      });
      window.location.href = data.redirect;
    } catch (err) {
      setBypassErr(err.response?.data?.detail || 'Could not redeem code.');
      setBypassSubmitting(false);
    }
  };

  return (
    <div className="ss-test-wrap" data-testid="ss-preview-screen">
      <div className="ss-test-container">
        <div className="ss-preview-card">
          <div className="ss-preview-score">
            <div>
              <div className="ss-hero-iq">Scam IQ preview</div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                <span className="ss-preview-iq">{metrics.score}</span>
                <span className="ss-preview-iq-suffix">/ 100</span>
              </div>
              <div className="ss-preview-rank">{metrics.rank}</div>
            </div>
            <ShieldCheck size={56} color="#16B1C4" strokeWidth={1.5} style={{ marginLeft: 'auto' }} />
          </div>
          <div className="ss-preview-divider"></div>
          <div className="ss-preview-row">
            <div>
              <div className="label">Strength</div>
              <div className="val">{labelFor(metrics.strong)}</div>
            </div>
            <div>
              <div className="label">Weak spot</div>
              <div className="val">{labelFor(metrics.weak)}</div>
            </div>
          </div>
          <p style={{ marginTop: 22, color: 'rgba(255,255,255,0.7)', fontSize: 14, lineHeight: 1.6 }}>
            {metrics.dangerous === 0
              ? "Strong start. You're already pausing on the basics. Unlock the full test to find your real weak spot."
              : "There's a clear pattern in where you got caught. The full ScamSmart training is built to fix exactly that."}
          </p>
        </div>

        <h2 className="ss-paywall-headline">Unlock your full ScamSmart training</h2>
        <p className="ss-paywall-sub">
          Get your full Scam IQ Report, train your weak spots, set up your Family Safe Word, use Scam Radar,
          and practise the full library of premium 7-layer scenarios — the calm, respectful way to help your family.
        </p>
        <ul className="ss-paywall-list" data-testid="ss-paywall-list">
          <li><CheckCircle2 size={18} className="ss-paywall-check" /> Full premium Scam IQ Test + Report</li>
          <li><CheckCircle2 size={18} className="ss-paywall-check" /> The complete premium scenario library</li>
          <li><CheckCircle2 size={18} className="ss-paywall-check" /> Scam Radar — paste a suspicious message, get a risk read</li>
          <li><CheckCircle2 size={18} className="ss-paywall-check" /> Family Safe Word setup + printable family card</li>
          <li><CheckCircle2 size={18} className="ss-paywall-check" /> Weekly Scam Drill — one fresh scenario each week</li>
          <li><CheckCircle2 size={18} className="ss-paywall-check" /> Family Scorecard — track every family member</li>
        </ul>
        <div className="ss-paywall-price">
          $14.99 AUD <small>one-off &middot; instant access</small>
        </div>

        <form onSubmit={onSubmit} data-testid="ss-paywall-form">
          <input
            type="email"
            className="ss-email-input"
            placeholder="Your email — we'll send your access here"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={submitting}
            required
            data-testid="ss-email-input"
          />
          {emailErr && <p style={{ color: '#c63d3d', fontSize: 13, marginBottom: 8 }}>{emailErr}</p>}
          <button
            type="submit"
            className="ss-btn-primary"
            disabled={submitting}
            style={{ width: '100%', justifyContent: 'center' }}
            data-testid="ss-paywall-cta"
          >
            {submitting ? <><Loader2 size={18} className="ss-spin" /> Starting checkout…</> : <><Lock size={16} /> Unlock Full Access — $14.99 AUD</>}
          </button>
        </form>

        <p className="ss-disclaimer">
          Secure payment via Stripe. ScamSmart is an awareness and decision-training tool — it cannot detect every
          scam or guarantee protection. Always verify important requests through official or trusted channels.
        </p>

        <div style={{ marginTop: 28 }}>
          {!showBypass ? (
            <div style={{
              background: 'rgba(249, 176, 62, 0.08)',
              border: '1px dashed rgba(249, 176, 62, 0.4)',
              borderRadius: 12,
              padding: '14px 16px',
              textAlign: 'center',
            }}>
              <p style={{ fontSize: 12, letterSpacing: 1.5, textTransform: 'uppercase', fontWeight: 800, color: '#c08220', margin: '0 0 4px' }}>
                Creator / early access
              </p>
              <button
                onClick={() => setShowBypass(true)}
                style={{
                  background: 'transparent', border: 'none', color: '#0A1F44',
                  fontSize: 13, textDecoration: 'underline', cursor: 'pointer',
                  fontFamily: 'inherit', fontWeight: 600,
                }}
                data-testid="ss-bypass-toggle"
              >
                Have an early-access code? Redeem here
              </button>
            </div>
          ) : (
            <form onSubmit={submitBypass} data-testid="ss-bypass-form" style={{ maxWidth: 380, margin: '0 auto' }}>
              <label style={{ fontSize: 11, letterSpacing: 1.5, textTransform: 'uppercase', fontWeight: 800, color: '#c08220', marginBottom: 6, display: 'block' }}>
                Early-access code
              </label>
              <input
                type="text"
                className="ss-email-input"
                placeholder="Enter your code"
                value={bypassCode}
                onChange={(e) => setBypassCode(e.target.value)}
                disabled={bypassSubmitting}
                autoComplete="off"
                data-testid="ss-bypass-code"
              />
              {bypassErr && <p style={{ color: '#c63d3d', fontSize: 12, margin: '0 0 8px' }}>{bypassErr}</p>}
              <button
                type="submit"
                className="ss-btn-gold"
                disabled={bypassSubmitting}
                style={{ width: '100%', justifyContent: 'center' }}
                data-testid="ss-bypass-submit"
              >
                {bypassSubmitting ? 'Unlocking…' : 'Redeem code & unlock'}
              </button>
            </form>
          )}
        </div>
      </div>

      <style>{`
        @keyframes ss-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .ss-spin { animation: ss-spin 1s linear infinite; }
      `}</style>
    </div>
  );
};

export default ScamSmartLanding;
